import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import type { CaseStatus } from "@/api";
import { Button } from "@/components/Button";
import { CheckIcon } from "@/components/icons";
import { NEXT_TARGET, stageOf, type ManualStage } from "@/lib/stages";

import { anchorInBand, placePopover, POPOVER_EDGE, visibleBand } from "./popoverPlacement";

/** `.popover`'s `max-inline-size` at a desk, in rem — the inline style keeps it (review final, 2). */
const POPOVER_MAX_REM = 22;
import { ACTION_WORD, StagePath } from "./StagePath";
import styles from "./Stages.module.css";

// The one gesture a register row offers, in words (design 2026-09-22 §8.1):
// `ACTION_WORD` — a verb, not the chip's state (UX gate round 1, #7; round
// 2, #8) — lives with the path since R5-9, which keeps each point's place
// as wide as the button with its verb.

/** Read out and shown on hover for the short „Ausgang …" (fix round 3, C1). */
const OUTCOME_NAME = "Ausgang festlegen: gewonnen oder verloren";

interface StageControlProps {
  caseId: string;
  status: CaseStatus;
  busy: boolean;
  onStage: (id: string, to: CaseStatus) => void;
  /** `path`: all five points (record head). `action`: only the one gesture possible now (register row). */
  variant?: "path" | "action";
  className?: string;
  /** The Vorgang the question names, so its own popover need not hide the amount it asks about (fix round 2, ruling c). */
  caseNo?: string;
  /** Already formatted (`formatMoney`/„noch nicht kalkuliert") — `StageControl` does not know volumes. */
  amountLabel?: string;
  /**
   * Where the focus goes once a gesture has landed and left no trigger
   * behind — „Gewonnen" leaves nothing to press (fix round 3). The register
   * hands in the row's own link; without it the focus stays where the
   * browser put it.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * The id of what the trigger acts on — the register row's title link
   * (fix round 4, M5): „Angebot raus" alone does not say which Vorgang to a
   * screen reader moving from button to button.
   */
  describedBy?: string;
  /**
   * What the mail asked for and the offer does not hold, in the question
   * before „Angebot raus" (UX-Gutachten 4) — rendered only while that
   * question is open. It reports through `onShown` whether something stands
   * under `id`; only then is the yes described by it (review final, 4).
   * Said, never locked.
   */
  sendWarnings?: (id: string, onShown: (shown: boolean) => void) => ReactNode;
  /**
   * Someone is about to ask „Angebot raus" — the trigger is hovered or
   * focused. The place that reads for `sendWarnings` reads ahead here
   * (review final, 3), so Enter, Enter does not outrun the list.
   */
  onSendIntent?: () => void;
}

/**
 * At most one of these stands open at a time, across every row (fix round
 * 2, finding 1) — a second „Angebot raus" or picker used to leave the first
 * one open too, both anchored, one now stale. A plain module value rather
 * than context: every instance already renders independently, and a second
 * provider layer for one boolean would outweigh it.
 */
let activeClose: (() => void) | null = null;

/** Marks every action trigger, so an outside press on another one is not swallowed (fix round 4, I2). */
const TRIGGER_ATTRIBUTE = "data-stage-trigger";

/**
 * A table's sticky head or foot, measured where it is **painted** (fix
 * round 4, I1). Round 3 measured `tHead`/`tFoot` — the row groups, which
 * never stick (only their cells do) and so reported their static place far
 * up or down the page: the popover neither flipped above the sticky sum nor
 * closed under the sticky head. The first cell of the first row is the band
 * itself. A cell that is not sticky (the phone's card layout lets the foot
 * scroll with the cards, and hides the head) does not narrow the band.
 */
function stickyRect(cell: Element | null | undefined) {
  if (cell === null || cell === undefined) return null;
  if (getComputedStyle(cell).position !== "sticky") return null;
  const { top, right, bottom, left, height } = cell.getBoundingClientRect();
  return { top, right, bottom, left, height };
}

/**
 * Swallows the one click that belongs to a press which only dismissed the
 * popover (fix round 4, I2): pressing a register row to close the question
 * used to open that row's record as well, and the search and filters were
 * gone. Capture phase on the document, so neither React's handlers nor a
 * link's navigation ever see it.
 *
 * Disarmed by the next press or key — a press that ends without a click
 * (a touch that turns into a scroll, a drag off the page) must not leave a
 * trap for a later, unrelated click; a key's own synthetic click (Enter on
 * a button) is never swallowed.
 */
function swallowNextClick() {
  function disarm() {
    document.removeEventListener("click", swallow, true);
    document.removeEventListener("pointerdown", disarm, true);
    document.removeEventListener("pointercancel", disarm, true);
    document.removeEventListener("keydown", disarm, true);
  }
  function swallow(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    disarm();
  }
  document.addEventListener("click", swallow, true);
  document.addEventListener("pointerdown", disarm, true);
  document.addEventListener("pointercancel", disarm, true);
  document.addEventListener("keydown", disarm, true);
}

/**
 * A floating panel for the `action` variant's question and picker (register
 * fix round 1, C1/I7; round 2, finding 1/5/ruling c; round 3): the trigger
 * sits in the register's stage cell, beside or under the chip (UX gate
 * round 2, #8), which is only a few rem wide, and the register's frame
 * clips at its own edge so head and foot can stick (F-2). Rendered into
 * `document.body` instead, anchored to the trigger.
 *
 * Measured, not guessed (fix round 3, Critical): it renders hidden first, a
 * layout effect reads its real size and the trigger's rect, and
 * `placePopover` decides — before the first paint, so nothing jumps. From
 * then on every scroll, resize or change in the table places it again with
 * direct style writes (no render per scroll event). It closes itself once
 * the trigger has scrolled out of sight, under the sticky head or off
 * screen, instead of floating over rows it no longer belongs to.
 *
 * `onClick` stops at its root (fix round 3): the panel portals out of the
 * row in the DOM but stays the row's React child, so its clicks would bubble
 * to whatever the row does on a click — the record's head gets the same
 * guard without having to know.
 */
function ActionPopover({
  anchorRef,
  onDismiss,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  /** Closes without asking for the focus back — the caller decides where it goes. */
  onDismiss: () => void;
  children: ReactNode;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  // The latest `onDismiss` for listeners bound once; written in an effect,
  // never during render (`react-hooks/refs`).
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useLayoutEffect(() => {
    const pop = popRef.current;
    const anchor = anchorRef.current;
    if (pop === null || anchor === null) return undefined;
    const table = anchor.closest("table");

    function place(opening = false) {
      if (pop === null || anchor === null) return;
      const root = document.documentElement;
      const viewportWidth = root.clientWidth || window.innerWidth;
      const viewportHeight = root.clientHeight || window.innerHeight;
      const band = visibleBand(
        viewportHeight,
        stickyRect(table?.tHead?.rows[0]?.cells[0]),
        stickyRect(table?.tFoot?.rows[0]?.cells[0]),
      );
      let anchorRect = anchor.getBoundingClientRect();
      if (opening && !anchorInBand(anchorRect, band)) {
        // Opened on a trigger the sticky head or foot half covers (Tab
        // scrolls only as far as the viewport's edge): bring it into sight
        // first rather than closing the popover the moment it opens.
        anchor.scrollIntoView({ block: "nearest" });
        anchorRect = anchor.getBoundingClientRect();
      } else if (!anchorInBand(anchorRect, band)) {
        // Out of sight: close, and if the keyboard was in here, park it on
        // the trigger without scrolling the page back to it.
        const hadFocus = pop.contains(document.activeElement);
        dismissRef.current();
        if (hadFocus) anchor.focus({ preventScroll: true });
        return;
      }
      // At most 22rem at a desk (the stylesheet's own bound, which an inline
      // style would otherwise override), and never wider than the viewport
      // less its edges (review final, 2).
      const rem = parseFloat(getComputedStyle(root).fontSize) || 16;
      pop.style.maxInlineSize = `${Math.max(0, Math.min(POPOVER_MAX_REM * rem, viewportWidth - 2 * POPOVER_EDGE))}px`;
      const size = pop.getBoundingClientRect();
      const at = placePopover(anchorRect, { width: size.width, height: size.height }, viewportWidth, band);
      pop.style.left = `${at.left}px`;
      pop.style.top = `${at.top}px`;
      pop.style.setProperty("--caret-x", `${at.caret}px`);
      pop.dataset.side = at.side;
      pop.dataset.placed = "true";
    }

    place(true);
    // Focus goes in once, when it is placed and visible — never again on a
    // later reposition, which would pull it back from wherever Tab went.
    pop.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus({ preventScroll: true });

    const replace = () => place();
    window.addEventListener("resize", replace);
    window.addEventListener("scroll", replace, true);
    const observer = new MutationObserver(replace);
    if (table !== null) observer.observe(table, { childList: true, subtree: true, attributes: true });
    // What it holds can grow after it was placed — the mail's list arrives
    // with its Vorgang (review final, 2): it is placed again, without
    // touching the focus. Not every test environment has the observer.
    const resized = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(replace);
    resized?.observe(pop);
    return () => {
      window.removeEventListener("resize", replace);
      window.removeEventListener("scroll", replace, true);
      observer.disconnect();
      resized?.disconnect();
    };
  }, [anchorRef]);

  // A minimal focus trap: the popover holds two or three buttons and Tab
  // must not leave it while it stands (fix round 1, C1).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const focusables = popRef.current?.querySelectorAll<HTMLElement>("button:not(:disabled)");
      if (focusables === undefined || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // A press anywhere outside the popover and its trigger closes it. The
  // focus goes back to the trigger **only** if it fell to <body> — a press
  // on the search field or another row's control keeps the focus there
  // (fix round 3: round 2 always refocused the trigger, a tick later, and so
  // took the focus away from the very field just clicked into).
  //
  // The click that follows the press is swallowed (fix round 4, I2): it was
  // spent on closing, not on the row or link under it. One exception —
  // another row's step trigger: that press means „this one instead", and
  // one popover replacing the other is a single click, as before.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      dismissRef.current();
      if (!(target instanceof Element && target.closest(`[${TRIGGER_ATTRIBUTE}]`) !== null)) swallowNextClick();
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active === null || active === document.body) anchorRef.current?.focus({ preventScroll: true });
      }, 0);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [anchorRef]);

  return createPortal(
    // Not interactive itself — the click handler only fences its buttons'
    // clicks in, so a row behind it never sees them.
    <div ref={popRef} className={styles.popover} onClick={(event) => event.stopPropagation()}>
      {children}
    </div>,
    document.body,
  );
}

/**
 * The three stages a person sets by hand (design 2026-09-07 §5.2) — the
 * „Unterlagen" tick, „Angebot raus" with its question, and the Gewonnen /
 * Verloren picker — lifted out of `CaseRow` so the register and the record
 * can offer them (design 2026-09-22 D-8).
 *
 * The question before „Angebot raus" is asked **inline** (UX ruling 3,
 * F-10), the way the calculation asks it: a system dialog in the middle of
 * the tool breaks the flow and cannot be styled or read like the rest.
 * Focus goes to „Ja, Angebot raus"; „Abbrechen", Escape and a click outside
 * put it back on the point that asked — for **either** question, the picker
 * included (fix round 1: the picker's own „Abbrechen" and its Escape used to
 * leave focus nowhere, because the return effect never re-ran for it).
 */
export function StageControl({
  caseId,
  status,
  busy,
  onStage,
  variant = "path",
  className,
  caseNo,
  amountLabel,
  returnFocusRef,
  describedBy,
  sendWarnings,
  onSendIntent,
}: StageControlProps) {
  const stage = stageOf(status);
  const warningsId = useId();
  const [warningsShown, setWarningsShown] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Set when a question closes without an answer; read once the trigger is
  // back in the DOM. A ref, not state: nothing renders from it.
  const refocus = useRef(false);
  // Set by a gesture while its answer is on the way — the focus is looked
  // after once it is back (fix round 3; round 4: once, not until the status
  // changes).
  const landing = useRef(false);
  // `choosing` alone survives the outcome landing (the same instance gets
  // `won`/`lost` from a refetch); `stage.next === "outcome"` is what says the
  // picker still belongs on screen. Not reset in an effect: a stale
  // `choosing` sits inert once this goes false. Same for `confirming`.
  const showOutcome = choosing && stage.next === "outcome";
  const showQuestion = confirming && stage.next === "sent";
  const isOpen = showOutcome || showQuestion;

  // Stable across the component's lifetime — `setChoosing`/`setConfirming`
  // never change identity, so this needs no dependency list to stay current.
  const closeSelf = useRef(() => {
    setChoosing(false);
    setConfirming(false);
  });

  function releaseIfMine() {
    if (activeClose === closeSelf.current) activeClose = null;
  }

  function closeWithFocusReturn() {
    refocus.current = true;
    closeSelf.current();
  }

  // Fix round 2, finding 1: only one popover open across the whole table —
  // opening this one closes whichever other row's was still open.
  function openExclusively(open: () => void) {
    if (activeClose !== null && activeClose !== closeSelf.current) activeClose();
    activeClose = closeSelf.current;
    open();
  }

  /** A gesture that sets a stage — the focus is looked after until it has landed. */
  function land(to: CaseStatus) {
    releaseIfMine();
    landing.current = true;
    closeSelf.current();
    onStage(caseId, to);
  }

  // Escape closes the picker or the question. On the document, because the
  // button that opened either is unmounted by the same state change.
  useEffect(() => {
    if (!isOpen) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      closeWithFocusReturn();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  // If the row itself disappears while its popover was the open one (status
  // changed elsewhere, a filter dropped it), release the claim so a stale
  // reference cannot no-op a future close. Mount/unmount only — a naive
  // `[isOpen]` dependency here would also fire this cleanup on the
  // false→true transition, clearing the claim `openExclusively` had just
  // set moments before.
  useEffect(() => {
    return () => releaseIfMine();
  }, []);

  // The popover focuses its first button once it has been placed (see
  // `ActionPopover` above) — in both variants since the record's head holds
  // the question in one too (UX gate round 4, #10). When either goes
  // without an answer, the trigger that asked gets it back — the only
  // button in the root (the popover is portalled out of it).
  useEffect(() => {
    if (isOpen || !refocus.current) return;
    refocus.current = false;
    rootRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [isOpen]);

  // Fix round 3: after „Ja, Angebot raus", „Gewonnen", „Verloren" or
  // „Unterlagen abhaken" the button just pressed is gone (or disabled while the
  // request runs) and the focus used to fall to <body> — a keyboard user
  // started over from the top of the page. Once the request is back, the
  // focus goes to this control's trigger again, or — when the new status
  // left no trigger („Gewonnen" leaves nothing to press) — to
  // `returnFocusRef`. Only ever while the focus is on <body>: a person who
  // has moved on somewhere else keeps their place.
  //
  // Handled exactly once, then forgotten (fix round 4, M1): `busy` covers
  // the request **and** the refreshed answer (`useStageChange`), so when it
  // drops the new status is already here. Round 3 kept the mark until the
  // status changed — after a refused step it never did, and the stale mark
  // pulled the focus to this row the next time any other row's request came
  // back.
  useEffect(() => {
    if (!landing.current || busy) return;
    landing.current = false;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    const trigger = rootRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)") ?? null;
    (trigger ?? returnFocusRef?.current ?? null)?.focus({ preventScroll: true });
  }, [busy, returnFocusRef]);

  function tick(which: ManualStage) {
    if (which === "outcome") {
      openExclusively(() => setChoosing(true));
      return;
    }
    if (which === "sent") {
      openExclusively(() => setConfirming(true));
      return;
    }
    land(NEXT_TARGET[which]);
  }

  // „2026-0072 · 124.967,10 €" — the figures in the mono face, like
  // everywhere else a number stands (CI §4). Both questions name the same
  // Vorgang and amount (fix round 3: the picker asked nothing at all).
  const subject =
    caseNo !== undefined ? (
      <>
        <span className="num">{caseNo}</span>
        {amountLabel !== undefined ? (
          <>
            {" · "}
            <span className="num">{amountLabel}</span>
          </>
        ) : null}
      </>
    ) : null;
  const subjectText = caseNo !== undefined ? `${caseNo}${amountLabel !== undefined ? ` · ${amountLabel}` : ""}` : null;

  const confirmGroup = (
    <div
      className={styles.confirm}
      role="group"
      aria-label={
        subjectText !== null ? `${subjectText} — Angebot als versendet markieren?` : "Angebot als versendet markieren?"
      }
    >
      <span className={styles.question}>{subject !== null ? <>{subject} — versendet?</> : "Versendet?"}</span>
      {confirming ? sendWarnings?.(warningsId, setWarningsShown) : null}
      <div className={styles.choices}>
        {/* Ink, not red (design 2026-10-24 §8.2): the question opens inside the
            stage path — beside the record head's own red way on, and in the
            rows of the register and the start list, where red would stand in
            the data. */}
        <Button
          variant="tinte"
          disabled={busy}
          onClick={() => land("sent")}
          aria-describedby={warningsShown ? warningsId : undefined}
        >
          Ja, Angebot raus
        </Button>
        <Button variant="quiet" disabled={busy} onClick={closeWithFocusReturn}>
          Abbrechen
        </Button>
      </div>
    </div>
  );

  const outcomeGroup = (
    <div
      className={styles.outcome}
      role="group"
      aria-label={subjectText !== null ? `${subjectText} — Ausgang des Angebots` : "Ausgang des Angebots"}
    >
      {subject !== null ? <span className={styles.question}>{subject} — Ausgang?</span> : null}
      <div className={styles.choices}>
        <button
          type="button"
          className={styles.choice}
          disabled={busy}
          onClick={() => land("won")}
        >
          Gewonnen
        </button>
        <button type="button" className={styles.choice} disabled={busy} onClick={() => land("lost")}>
          Verloren
        </button>
        {/* Neither outcome can be taken back, so the way out sets nothing. */}
        <Button variant="quiet" disabled={busy} onClick={closeWithFocusReturn}>
          Abbrechen
        </Button>
      </div>
    </div>
  );

  // The trigger stays enabled while its popover stands (fix round 3): it
  // is what the popover hangs from, a dimmed one read as „geht nicht",
  // and a second press closes the popover again. Tab cannot reach it
  // meanwhile — the popover holds the focus.
  const next = stage.resting ? null : stage.next;
  const opens = next !== null && next !== "documents";
  const trigger =
    next === null ? null : (
      <button
        ref={triggerRef}
        type="button"
        className={styles.action}
        data-stage-trigger=""
        data-stage-next={next}
        disabled={busy}
        aria-expanded={opens ? isOpen : undefined}
        aria-describedby={describedBy}
        aria-label={next === "outcome" ? OUTCOME_NAME : undefined}
        title={next === "outcome" ? OUTCOME_NAME : undefined}
        onClick={() => (isOpen ? closeWithFocusReturn() : tick(next))}
        onPointerEnter={next === "sent" ? onSendIntent : undefined}
        onFocus={next === "sent" ? onSendIntent : undefined}
      >
        {/* A check mark only for „Angebot raus" — „Unterlagen abhaken" says
            its verb (UX gate round 2, #8), and the picker behind „Ausgang …"
            asks first which one. From icons.tsx, never a glyph (ruling c). */}
        {next === "sent" ? <CheckIcon size={12} className={styles.actionIcon} /> : null}
        {ACTION_WORD[next]}
      </button>
    );

  if (variant === "action") {
    // Nothing to press: nothing rendered, so a card's step strip (`:empty`) folds away.
    if (trigger === null) return null;
    return (
      <div ref={rootRef} className={className}>
        {trigger}
        {isOpen ? (
          <ActionPopover anchorRef={triggerRef} onDismiss={() => closeSelf.current()}>
            {showQuestion ? confirmGroup : outcomeGroup}
          </ActionPopover>
        ) : null}
      </div>
    );
  }

  // The path shows where the Vorgang stands; the next step is a button in
  // the place of its point (UX gate round 4, #10) — the same gesture and
  // the same word as in a register row. Under the path it was a row of its
  // own that came and went with the stage, and the record's head grew and
  // shrank with it. Its question or picker opens in the same floating
  // panel as a register row's, anchored to the button: nothing in the head
  // moves while it asks.
  return (
    <div ref={rootRef} className={`${styles.control} ${className ?? ""}`}>
      <StagePath stage={stage} action={trigger} />
      {isOpen ? (
        <ActionPopover anchorRef={triggerRef} onDismiss={() => closeSelf.current()}>
          {showQuestion ? confirmGroup : outcomeGroup}
        </ActionPopover>
      ) : null}
    </div>
  );
}
